<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { Film } from "../../lib/layeredCanvas/dataModels/film";
  import type { FilmProceduralEffectType } from "../../lib/layeredCanvas/dataModels/proceduralEffects";
  import { createProceduralEffect, proceduralEffectParamSpecs } from "../../lib/layeredCanvas/dataModels/proceduralEffects";
  import type { FilmProceduralParamValue } from "../../lib/layeredCanvas/dataModels/proceduralEffects";
  import FilmProceduralNumberParam from './FilmProceduralNumberParam.svelte';
  import FilmProceduralColorParam from './FilmProceduralColorParam.svelte';
  import FilmProceduralTextParam from './FilmProceduralTextParam.svelte';
  import { _ } from 'svelte-i18n';

  export let film: Film;

  const dispatch = createEventDispatcher<{ change: void }>();

  let type: FilmProceduralEffectType = 'motion-lines';
  let params: Record<string, FilmProceduralParamValue> = {};
  let syncing = false;
  const idPrefix = `film-procedural-${Math.random().toString(36).slice(2, 8)}`;

  $: if (film.content.kind === 'procedural') {
      syncing = true;
      type = film.content.effect.type;
      params = { ...film.content.effect.params };
      syncing = false;
    }

  function updateFilm() {
    if (syncing || film.content.kind !== 'procedural') { return; }
    const effect = createProceduralEffect(type, params);
    film.proceduralEffect = effect;
    dispatch('change');
  }


  function numberValueOf(key: string, fallback: number): number {
    const value = params[key];
    if (Array.isArray(value)) {
      return fallback;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return fallback;
  }

  function stringValueOf(key: string, fallback: string): string {
    const value = params[key];
    if (Array.isArray(value)) {
      return fallback;
    }
    return typeof value === 'string' ? value : fallback;
  }

  function onNumberParamChange(key: string, value: number, step: number) {
    const previous = Number(params[key]);
    const tolerance = step >= 1 ? 0 : Math.max(step / 100, 1e-6);
    if (Number.isFinite(previous) && Math.abs(previous - value) <= tolerance) {
      return;
    }
    params = { ...params, [key]: value };
    updateFilm();
  }

  function onColorParamChange(key: string, value: string) {
    const previous = typeof params[key] === 'string' ? (params[key] as string) : '';
    if (previous.toLowerCase() === value.toLowerCase()) {
      return;
    }
    params = { ...params, [key]: value };
    updateFilm();
  }

  function onTextParamChange(key: string, value: string) {
    const previous = typeof params[key] === 'string' ? (params[key] as string) : '';
    if (previous === value) {
      return;
    }
    params = { ...params, [key]: value };
    updateFilm();
  }
</script>

{#if film.content.kind === 'procedural'}
<div class="procedural-controls">
  <div class="selector-row">
    <div class="selector-label">{$_('film.procedural.selectorLabel')}</div>
    <div class="type-display">
      {#if type === 'motion-lines'}
        {$_('film.procedural.type.motion-lines')}
      {:else if type === 'speed-lines'}
        {$_('film.procedural.type.speed-lines')}
      {:else if type === 'dots'}
        {$_('film.procedural.type.dots')}
      {:else}
        {type}
      {/if}
    </div>
  </div>

  {#each Object.entries(proceduralEffectParamSpecs[type]) as [key, spec]}
    {#if spec.kind === 'number'}
      <FilmProceduralNumberParam
        label={spec.labelKey ? $_(spec.labelKey) : spec.label}
        value={numberValueOf(key, spec.min)}
        min={spec.min}
        max={spec.max}
        step={spec.step}
        on:change={(event) => onNumberParamChange(key, event.detail, spec.step)}
      />
    {:else if spec.kind === 'color'}
      <FilmProceduralColorParam
        label={spec.labelKey ? $_(spec.labelKey) : spec.label}
        value={stringValueOf(key, '#000000')}
        on:change={(event) => onColorParamChange(key, event.detail)}
      />
    {:else if spec.kind === 'text'}
      <FilmProceduralTextParam
        label={spec.labelKey ? $_(spec.labelKey) : spec.label}
        value={stringValueOf(key, '')}
        placeholder={spec.placeholderKey ? $_(spec.placeholderKey) : spec.placeholder}
        on:change={(event) => onTextParamChange(key, event.detail)}
      />
    {/if}
  {/each}
</div>
{/if}

<style>
  .procedural-controls {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .selector-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .selector-label {
    font-size: 14px;
    min-width: 92px;
    white-space: nowrap;
  }
  .type-display {
    flex: 1;
    min-height: 28px;
    font-size: 0.9rem;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid rgb(var(--color-surface-300));
    background: rgb(var(--color-surface-100));
    color: rgb(var(--color-surface-700));
    display: flex;
    align-items: center;
  }
</style>
